<script lang="ts">
  import { onMount } from "svelte";
  import { api, type TaskListItem } from "../lib/api";

  let { filter, onback, onhome }: { filter?: string; onback?: () => void; onhome?: () => void } = $props();

  let items = $state<TaskListItem[]>([]);
  let total = $state(0);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let removing = $state<number | null>(null);
  let menuFor = $state<number | null>(null);

  function notifyDealChanged(leadAmoId: number | null) {
    if (!leadAmoId) return;
    window.dispatchEvent(new CustomEvent("kvin:deal-changed", { detail: { leadAmoId } }));
  }

  async function completeTask(t: TaskListItem) {
    removing = t.amo_id;
    menuFor = null;
    try {
      await api.completeTask(t.amo_id);
      items = items.filter((x) => x.amo_id !== t.amo_id);
      total = Math.max(0, total - 1);
      notifyDealChanged(t.lead_amo_id);
    } catch (e) { /* soft fail */ }
    finally { removing = null; }
  }

  async function deleteTask(t: TaskListItem) {
    if (!confirm(`Удалить задачу?\n\n«${t.text.slice(0, 80)}»\n\nЗадача будет закрыта в amoCRM с пометкой «Удалено через Квин» и исчезнет из всех списков.`)) return;
    removing = t.amo_id;
    menuFor = null;
    try {
      await api.deleteTask(t.amo_id);
      items = items.filter((x) => x.amo_id !== t.amo_id);
      total = Math.max(0, total - 1);
      notifyDealChanged(t.lead_amo_id);
    } catch (e) {
      alert(`Не удалось удалить: ${e instanceof Error ? e.message : "ошибка"}`);
    } finally { removing = null; }
  }

  async function pokeOwner(t: TaskListItem) {
    const tg = (window as any).Telegram?.WebApp;
    const confirmText =
      `Пнуть ${t.responsible_name} по задаче?\n\n` +
      `«${t.text.slice(0, 80)}»\n\n` +
      `В amoCRM появятся заметка и срочная задача. Если не выполнит в течение 2 часов — автонапоминания каждые 2 часа (до 3 раз).`;
    let ok = false;
    if (tg?.showConfirm) {
      ok = await new Promise<boolean>((resolve) => tg.showConfirm(confirmText, (v: boolean) => resolve(!!v)));
    } else {
      ok = confirm(confirmText);
    }
    if (!ok) return;
    removing = t.amo_id;
    menuFor = null;
    try {
      const r = await api.pokeTaskOwner(t.db_id);
      const parts: string[] = [];
      if (r.note_added) parts.push("заметка");
      if (r.urgent_task_amo_id) parts.push("срочная задача");
      if (r.sms && r.sms.ok) parts.push("SMS");
      const msg = `✓ Пнули ${r.manager_name}: ${parts.join(" + ") || "ничего"}`;
      if (tg?.showAlert) tg.showAlert(msg);
      else alert(msg);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ошибка";
      if (tg?.showAlert) tg.showAlert(`Не удалось пнуть: ${msg}`);
      else alert(`Не удалось пнуть: ${msg}`);
    } finally { removing = null; }
  }

  function openInAmo(t: TaskListItem) {
    menuFor = null;
    if (!t.amo_url) {
      alert("У задачи нет привязки к сделке/контакту в amoCRM");
      return;
    }
    // TG WebApp openLink — открывает в внешнем браузере, не внутри webview
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(t.amo_url);
    else window.open(t.amo_url, "_blank", "noopener");
  }

  function dueFmt(iso: string | null, overdueDays: number): string {
    if (!iso) return "—";
    const d = new Date(iso);
    const date = d.toLocaleDateString("ru", { day: "numeric", month: "short" });
    const time = d.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" });
    if (overdueDays > 0) return `${date}, ${time}`;
    const delta = (d.getTime() - Date.now()) / 1000;
    if (delta < 0) {
      const overdueH = Math.floor(-delta / 3600);
      if (overdueH >= 1) return `просрочено ${overdueH}ч · ${time}`;
      const overdueM = Math.max(1, Math.floor(-delta / 60));
      return `просрочено ${overdueM} мин · ${time}`;
    }
    if (delta < 3600) return `через ${Math.floor(delta / 60)} мин`;
    if (delta < 86400) return `через ${Math.floor(delta / 3600)}ч`;
    return `${date}, ${time}`;
  }

  const title = $derived(filter === "overdue" ? "Просроченные задачи" : "Задачи");

  onMount(async () => {
    try {
      const r = await api.tasksList(filter);
      items = r.items;
      total = r.total;
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">{title} · {total}</div>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {/if}
  </header>

  {#if loading}
    <div class="loading">Загружаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if items.length === 0}
    <div class="empty">Задач нет</div>
  {:else}
    <section class="list">
      {#each items as t}
        <div class="row-wrap">
          {#if t.lead_amo_id}
            <a class="row" class:overdue={t.overdue_days > 0} href={`?deal=${t.lead_amo_id}`}>
              {#if t.overdue_days > 0}
                <div class="ovd-badge">{t.overdue_days}<span class="ovd-unit">дн</span></div>
              {/if}
              <div class="row-main">
                <div class="row-text">{t.text}</div>
                <div class="row-meta">
                  <span class="resp">{t.responsible_name}</span>
                  <span class="sep"></span>
                  <span class="lead">{t.lead_name}</span>
                  <span class="sep"></span>
                  <span class="due" class:overdue={t.overdue_days > 0}>{dueFmt(t.complete_till, t.overdue_days)}</span>
                </div>
              </div>
            </a>
          {:else if t.amo_url}
            <button class="row floating clickable" type="button" onclick={() => openInAmo(t)}>
              {#if t.overdue_days > 0}
                <div class="ovd-badge">{t.overdue_days}<span class="ovd-unit">дн</span></div>
              {/if}
              <div class="row-main">
                <div class="row-text">{t.text}</div>
                <div class="row-meta">
                  <span class="resp">{t.responsible_name}</span>
                  <span class="sep"></span>
                  <span class="row-floating">
                    {#if t.entity_type === "contacts"}контакт →{:else if t.entity_type === "companies"}компания →{:else}открыть в amoCRM →{/if}
                  </span>
                  <span class="sep"></span>
                  <span class="due" class:overdue={t.overdue_days > 0}>{dueFmt(t.complete_till, t.overdue_days)}</span>
                </div>
              </div>
            </button>
          {:else}
            <div class="row floating">
              {#if t.overdue_days > 0}
                <div class="ovd-badge">{t.overdue_days}<span class="ovd-unit">дн</span></div>
              {/if}
              <div class="row-main">
                <div class="row-text">{t.text}</div>
                <div class="row-meta">
                  <span class="resp">{t.responsible_name}</span>
                  <span class="sep"></span>
                  <span class="row-floating">без сделки</span>
                  <span class="sep"></span>
                  <span class="due" class:overdue={t.overdue_days > 0}>{dueFmt(t.complete_till, t.overdue_days)}</span>
                </div>
              </div>
            </div>
          {/if}
          <button class="kebab" type="button" aria-label="Действия"
                  disabled={removing === t.amo_id}
                  onclick={(e) => { e.stopPropagation(); menuFor = menuFor === t.amo_id ? null : t.amo_id; }}>
            {#if removing === t.amo_id}⋯{:else}⋮{/if}
          </button>
          {#if menuFor === t.amo_id}
            <div class="menu" onclick={(e) => e.stopPropagation()} role="menu">
              {#if t.overdue_days > 0 || (t.complete_till && new Date(t.complete_till).getTime() < Date.now())}
                <button onclick={() => pokeOwner(t)} type="button" class="poke">🔔 Пнуть {t.responsible_name}</button>
              {/if}
              {#if t.amo_url}
                <button onclick={() => openInAmo(t)} type="button">🔗 Открыть в amoCRM</button>
              {/if}
              <button onclick={() => completeTask(t)} type="button">Снять (выполнить)</button>
              <button onclick={() => deleteTask(t)} type="button" class="danger">Удалить</button>
              <button onclick={() => menuFor = null} type="button" class="ghost">Отмена</button>
            </div>
          {/if}
        </div>
      {/each}
    </section>
  {/if}
</div>

<style>
  .screen { padding-bottom: 80px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 15px; font-weight: 700;
    flex: 1; text-align: center;
  }
  .loading, .error-box, .empty { padding: 40px 16px; text-align: center; color: var(--text-mute); font-size: 13px; }
  .error-box { color: var(--danger); }

  .list { padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; }
  .row-wrap {
    position: relative;
    display: flex; gap: 8px; align-items: stretch;
  }
  .row {
    flex: 1; min-width: 0;
    display: flex; gap: 10px; align-items: center;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
    transition: transform 80ms ease;
  }
  .kebab {
    flex-shrink: 0;
    width: 36px;
    align-self: stretch;
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 12px;
    color: var(--text-mute);
    font-size: 22px; font-weight: 700;
    cursor: pointer;
    line-height: 1;
    transition: all 120ms;
  }
  .kebab:active { transform: scale(0.94); }
  .kebab:hover { color: var(--text); border-color: var(--ember); }
  .kebab:disabled { opacity: 0.5; cursor: wait; }
  .menu {
    position: absolute;
    right: 0; top: 100%; margin-top: 4px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: var(--shadow-lg);
    z-index: 30; min-width: 200px; overflow: hidden;
  }
  .menu button {
    display: block; width: 100%;
    background: transparent; border: none;
    text-align: left;
    padding: 12px 14px;
    color: var(--text);
    font-family: 'Manrope', sans-serif;
    font-size: 14px; cursor: pointer;
  }
  .menu button:not(:last-child) { border-bottom: 1px solid var(--border); }
  .menu button:active { background: var(--bg-elev-2); }
  .menu button.danger { color: var(--danger); }
  .menu button.ghost { color: var(--text-mute); }
  .menu button.poke { color: var(--ember); font-weight: 600; }
  .row:active { transform: scale(0.985); }
  .row.overdue { border-left: 3px solid var(--danger); }
  .row.floating { border-left: 3px solid var(--text-dim); }
  button.row.clickable {
    cursor: pointer;
    font: inherit;
    text-align: left;
  }
  button.row.clickable:active { transform: scale(0.985); }
  .lead { color: var(--brass); font-weight: 600; }
  .row-floating {
    color: var(--text-dim);
    font-style: italic;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .ovd-badge {
    flex-shrink: 0;
    min-width: 44px; height: 44px;
    border-radius: 10px;
    background: var(--danger); color: #fff;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700; font-size: 15px;
    line-height: 1; padding: 0 6px;
  }
  .ovd-unit { font-size: 9px; opacity: 0.85; margin-top: 2px; font-weight: 500; }
  .row-main { flex: 1; min-width: 0; }
  .row-text {
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
  }
  .row-meta {
    margin-top: 4px;
    font-size: 11px; color: var(--text-mute);
    display: flex; align-items: center; gap: 8px;
    flex-wrap: wrap;
  }
  .resp { color: var(--brass); font-weight: 600; }
  .due.overdue { color: var(--danger); font-weight: 600; }
  .sep { width: 3px; height: 3px; background: var(--text-dim); border-radius: 50%; }
</style>
